import { Property, RevenueManagerBranch } from '@/types/revenue-manager';
import { delay, API_ENDPOINTS, apiClient, ApiResponse, USE_MOCK } from './client';
import { mockPropertiesData } from '@/data/mock-data';

export type CmProperty = Record<string, unknown>;

export interface PropertiesResponse {
  success: boolean;
  data: Property[];
  total: number;
}

// ============ API Functions ============

export async function fetchProperties(): Promise<PropertiesResponse> {
  if (USE_MOCK) {
    await delay();
    return {
      success: true,
      data: mockPropertiesData,
      total: mockPropertiesData.length,
    };
  }
  
  const response = await apiClient.get<ApiResponse<Property[]>>(API_ENDPOINTS.properties.list);
  return {
    success: response.data.success,
    data: response.data.data,
    total: response.data.data.length,
  };
}

export async function fetchPropertyById(propertyId: string): Promise<ApiResponse<Property | null>> {
  if (USE_MOCK) {
    await delay(200);
    const property = mockPropertiesData.find(p => p.id === propertyId);
    return {
      success: !!property,
      data: property || null,
    };
  }
  
  const response = await apiClient.get<ApiResponse<Property | null>>(
    API_ENDPOINTS.properties.get(propertyId)
  );
  return response.data;
}

export async function fetchCmPropertyById(propertyId: string): Promise<ApiResponse<CmProperty | null>> {
  if (USE_MOCK) {
    await delay(200);
    return {
      success: false,
      data: null,
      message: 'Mock mode does not support CM property detail lookup.',
    };
  }

  const response = await apiClient.get<ApiResponse<CmProperty | null>>(
    API_ENDPOINTS.properties.cmGet(propertyId)
  );
  return response.data;
}

export async function createProperty(property: Omit<Property, 'id'>): Promise<ApiResponse<Property>> {
  if (USE_MOCK) {
    await delay();
    const newProperty: Property = {
      ...property,
      id: `prop-${Date.now()}`,
    };
    mockPropertiesData.push(newProperty);
    return {
      success: true,
      data: newProperty,
    };
  }
  
  const response = await apiClient.post<ApiResponse<Property>>(
    API_ENDPOINTS.properties.create,
    property
  );
  return response.data;
}

export async function updateProperty(
  propertyId: string, 
  updates: Partial<Property>
): Promise<ApiResponse<Property | null>> {
  if (USE_MOCK) {
    await delay();
    const index = mockPropertiesData.findIndex(p => p.id === propertyId);
    if (index === -1) {
      return { success: false, data: null };
    }
    mockPropertiesData[index] = { ...mockPropertiesData[index], ...updates };
    return {
      success: true,
      data: mockPropertiesData[index],
    };
  }
  
  const response = await apiClient.patch<ApiResponse<Property | null>>(
    API_ENDPOINTS.properties.update(propertyId),
    updates
  );
  return response.data;
}

export async function deleteProperty(propertyId: string): Promise<ApiResponse<null>> {
  if (USE_MOCK) {
    await delay();
    const index = mockPropertiesData.findIndex(p => p.id === propertyId);
    if (index !== -1) {
      mockPropertiesData.splice(index, 1);
    }
    return { success: true, data: null };
  }
  
  const response = await apiClient.delete<ApiResponse<null>>(
    API_ENDPOINTS.properties.delete(propertyId)
  );
  return response.data;
}

export interface RevenueManagerBranchListResponse {
  success: boolean;
  data: RevenueManagerBranch[];
}

/**
 * Fetch revenue manager branch list
 */
export async function fetchRevenueManagerBranchList(): Promise<RevenueManagerBranchListResponse> {
  if (USE_MOCK) {
    await delay();
    // Mock data matching the API response structure
    const mockBranches: RevenueManagerBranch[] = [
      {
        id: 86,
        api_key: "key_86",
        org_id: 107,
        branch_name: "Hotel Sakura Mountain view Near Darjeeling Toy Train Station",
        cm_property_id: "39417267-a7ee-4e22-9187-98c5619e9efe"
      },
      {
        id: 87,
        api_key: "key_87",
        org_id: 107,
        branch_name: "Demo Property for Testing",
        cm_property_id: "12345678-1234-1234-1234-123456789abc"
      }
    ];
    return {
      success: true,
      data: mockBranches,
    };
  }
  
  const response = await apiClient.get<RevenueManagerBranchListResponse>(
    API_ENDPOINTS.properties.list
  );
  return response.data;
}
